import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  Cpu as CpuIcon,
  FileText as FileTextIcon,
  Search as SearchIcon,
  Shield as ShieldIcon
} from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { decodeRouteParam } from '@atbash-labs/plantview-links'
import {
  collectAdditionalProperties,
  filterSolverDiagrams,
  useGetPlantLogicSolverDetailQuery,
  type PlantFunctionLogicDiagramSummary,
  type PlantProtectiveFunction
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table'
import { Empty } from '../../components/primitives/Empty'
import {
  plantFunctionLogicDiagramHref,
  plantProtectiveFunctionHref,
  plantSafetyIndexHref
} from '../../lib/plant-links'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { SectionHeader } from './entity/SectionHeader'
import { PlantHref } from './PlantHref'

/**
 * `/plant/safety/solvers/:solverId` — logic-solver detail.
 *
 * Searchable FLD table (default: diagrams that `IMPLEMENTED_BY` a SIF)
 * plus the SRS protective functions that `SOLVED_BY` this solver.
 */
const LogicSolverDetailPage = (): React.ReactElement => {
  const { solverId = '' } = useParams<{ solverId: string }>()
  const decodedSolverId = decodeRouteParam(solverId)
  const { data, isLoading, isError } = useGetPlantLogicSolverDetailQuery(decodedSolverId, {
    skip: !decodedSolverId
  })

  const solver = data?.solver
  const diagrams = React.useMemo(() => data?.diagrams ?? [], [data?.diagrams])
  const functions = React.useMemo(() => data?.functions ?? [], [data?.functions])
  const additionalProperties = React.useMemo(
    () =>
      solver
        ? collectAdditionalProperties('LogicSolver', solver as unknown as Record<string, unknown>)
        : [],
    [solver]
  )

  return (
    <div className="flex flex-1 flex-col gap-card">
      <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
        <Link to={plantSafetyIndexHref()} className="hover:text-ink hover:underline">
          Safety
        </Link>
        <span className="mx-1.5 text-ink-4">/</span>
        <span className="text-ink">{solver?.name ?? decodedSolverId}</span>
      </nav>

      {isError ? (
        <DetailFallback
          title="Couldn't load this logic solver"
          hint="The solver may have been deleted or the proxy is unreachable."
        />
      ) : isLoading || !data ? (
        <DetailFallback title="Loading logic solver…" hint="" muted />
      ) : (
        <>
          <header className="flex items-start gap-4 rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
            <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
              <CpuIcon size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h1 className="m-0 min-w-0 break-words text-2xl font-semibold text-ink">
                  {solver?.name ?? decodedSolverId}
                </h1>
                {solver?.type && <Badge variant="secondary">{solver.type}</Badge>}
                {solver?.application && <Badge variant="outline">{solver.application}</Badge>}
              </div>
              <p className="m-0 mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-ink-3">
                {solver?.vendor && <span>{solver.vendor}</span>}
                {data.site && (
                  <>
                    {solver?.vendor && <span className="text-ink-4">·</span>}
                    <PlantHref
                      id={data.site.id}
                      label="Site"
                      node={data.site}
                      className="hover:text-ink hover:underline"
                    >
                      {data.site.name}
                    </PlantHref>
                  </>
                )}
              </p>
            </div>
          </header>

          <DiagramSection diagrams={diagrams} />
          <FunctionSection functions={functions} />

          {solver && (
            <EntityDetailsInspector entity={solver} additionalProperties={additionalProperties} />
          )}
        </>
      )}
    </div>
  )
}

function DiagramSection({
  diagrams
}: {
  diagrams: PlantFunctionLogicDiagramSummary[]
}): React.ReactElement {
  const navigate = useNavigate()
  const [search, setSearch] = React.useState('')
  const [linkedOnly, setLinkedOnly] = React.useState(true)
  const linkedCount = diagrams.filter((diagram) => diagram.implementedBy.length > 0).length
  const shown = React.useMemo(
    () => filterSolverDiagrams(diagrams, search, linkedOnly),
    [diagrams, search, linkedOnly]
  )

  return (
    <section>
      <SectionHeader
        label="Function logic diagrams"
        subtitle={diagramSubtitle(shown.length, diagrams.length, linkedOnly, search)}
      />
      {diagrams.length === 0 ? (
        <EmptyCard
          title="No function logic diagrams"
          hint="Ingest a Safety Manager points dump to attach FLDs to this solver."
        />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
          <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2.5">
            <SearchIcon size={14} className="text-ink-3" aria-hidden />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search FLD, number, or implementing SIF…"
              className="h-8 max-w-sm text-xs"
            />
            <div className="ml-auto flex items-center gap-1">
              <Button
                type="button"
                size="xs"
                variant={linkedOnly ? 'secondary' : 'ghost'}
                onClick={() => setLinkedOnly(true)}
              >
                Linked to a SIF ({linkedCount})
              </Button>
              <Button
                type="button"
                size="xs"
                variant={linkedOnly ? 'ghost' : 'secondary'}
                onClick={() => setLinkedOnly(false)}
              >
                All ({diagrams.length})
              </Button>
            </div>
          </div>
          {shown.length === 0 ? (
            <div className="px-3.5 py-6">
              <Empty
                title={linkedOnly ? 'No FLDs linked to a safety function' : 'No matching FLDs'}
                hint={
                  linkedOnly
                    ? 'Switch to All to browse the solver catalog, or run the Safety Manager ↔ SRS mapping job.'
                    : 'Try a different search.'
                }
                icon={FileTextIcon}
              />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[12%]">FLD</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Implemented by</TableHead>
                  <TableHead className="w-[12%] text-right">Points</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((diagram) => (
                  <TableRow
                    key={diagram.id}
                    className="cursor-pointer"
                    onClick={() => navigate(plantFunctionLogicDiagramHref(diagram.id))}
                  >
                    <TableCell className="tabular-nums text-ink-3">
                      {diagram.fld_number ?? '—'}
                    </TableCell>
                    <TableCell className="max-w-0">
                      <Link
                        to={plantFunctionLogicDiagramHref(diagram.id)}
                        className="inline-flex min-w-0 items-center gap-2 font-medium text-ink no-underline hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileTextIcon size={14} className="shrink-0 text-ink-4" aria-hidden />
                        <span className="truncate">{diagram.name}</span>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <ImplementedByLinks functions={diagram.implementedBy} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{diagram.pointCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </section>
  )
}

function ImplementedByLinks({
  functions
}: {
  functions: PlantProtectiveFunction[]
}): React.ReactElement {
  if (functions.length === 0) return <span className="text-ink-4">—</span>
  return (
    <div className="flex flex-col gap-0.5">
      {functions.slice(0, 3).map((fn) => (
        <Link
          key={fn.id}
          to={plantProtectiveFunctionHref(fn.id)}
          className="inline-flex min-w-0 items-center gap-1 text-ink no-underline hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <ShieldIcon size={11} className="shrink-0 text-ink-4" aria-hidden />
          <span className="truncate">{fn.name}</span>
        </Link>
      ))}
      {functions.length > 3 && (
        <span className="text-11 text-ink-3">+{functions.length - 3} more</span>
      )}
    </div>
  )
}

function FunctionSection({
  functions
}: {
  functions: PlantProtectiveFunction[]
}): React.ReactElement {
  return (
    <section>
      <SectionHeader
        label="Protective functions"
        subtitle={`${functions.length} ${functions.length === 1 ? 'function' : 'functions'}`}
      />
      {functions.length === 0 ? (
        <EmptyCard
          title="No protective functions"
          hint="SRS ingest links SIFs to this solver via SOLVED_BY."
        />
      ) : (
        <div className="flex flex-col gap-2">
          {functions.map((fn) => (
            <Link
              key={fn.id}
              to={plantProtectiveFunctionHref(fn.id)}
              className="flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
                <ShieldIcon size={16} aria-hidden />
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
                {fn.name}
              </span>
              {fn.sil && <Badge variant="outline">SIL {fn.sil}</Badge>}
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}

function diagramSubtitle(
  shown: number,
  total: number,
  linkedOnly: boolean,
  search: string
): string {
  const suffix = linkedOnly ? ' linked to a SIF' : ''
  if (search.trim() && shown !== total) return `${shown} of ${total} FLDs${suffix}`
  if (linkedOnly && shown !== total) return `${shown} of ${total} FLDs${suffix}`
  return `${shown} ${shown === 1 ? 'FLD' : 'FLDs'}${suffix}`
}

function EmptyCard({ title, hint }: { title: string; hint: string }): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      <Empty title={title} hint={hint} icon={CpuIcon} />
    </div>
  )
}

function DetailFallback({
  title,
  hint,
  muted
}: {
  title: string
  hint: string
  muted?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      {muted ? (
        <div className="text-sm text-ink-3">{title}</div>
      ) : (
        <Empty title={title} hint={hint} icon={WarningCircleIcon} />
      )}
    </div>
  )
}

export default LogicSolverDetailPage
